import * as React from 'react';
import type { Row } from '@tanstack/react-table';
import { Box, VisuallyHidden } from '@leagueplatform/genesis-core';
import { ChevronDownFilledIcon } from '../../assets/chevron-down-filled-icon';
import { IconButton } from '../icon-button/icon-button.component';

export type TableExpandColumnHeaderProps = {
  expandHeaderLabel?: string;
};

export function TableExpandColumnHeader({
  expandHeaderLabel,
}: TableExpandColumnHeaderProps) {
  return <VisuallyHidden>{expandHeaderLabel}</VisuallyHidden>;
}

export type TableExpandColumnCellProps<TData> = {
  row: Row<TData>;
  expandButtonLabel: string;
  collapseButtonLabel: string;
};

export function TableExpandColumnCell<TData>({
  row,
  expandButtonLabel,
  collapseButtonLabel,
}: TableExpandColumnCellProps<TData>) {
  const isExpanded = row.getIsExpanded();

  return (
    <IconButton
      label={isExpanded ? collapseButtonLabel : expandButtonLabel}
      onClick={row.getToggleExpandedHandler()}
      icon={<ChevronDownFilledIcon />}
      priority="tertiary"
      quiet
      size="small"
      ariaExpanded={isExpanded}
      css={{
        padding: '$one',
        '.collapsible-button-icon': {
          transition: 'transform 200ms',
        },
        ...(isExpanded && {
          transform: 'rotate(180deg)',
        }),
      }}
    />
  );
}

export type TableExpandedRowProps<TData> = {
  row: Row<TData>;
  colSpan: number;
  renderExpandedContent: (rowData: TData) => React.ReactNode;
};

export function TableExpandedRow<TData>({
  row,
  colSpan,
  renderExpandedContent,
}: TableExpandedRowProps<TData>) {
  if (!row.getIsExpanded()) {
    return null;
  }

  return (
    <Box as="tr">
      <Box
        as="td"
        colSpan={colSpan}
        css={{
          padding: '$one',
          backgroundColor: '$surfaceBackgroundSecondary',
          borderBottom: '1px solid $onSurfaceBorderSubdued',
        }}
      >
        {renderExpandedContent(row.original)}
      </Box>
    </Box>
  );
}
